import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Button } from '@hologram-dimension/button';
import { Form } from '@hologram-dimension/form';
import { FormField } from '@hologram-dimension/form-field';
import { Select } from '@hologram-dimension/select';
import { SelectField } from '@hologram-dimension/select-field';
import { TextInput } from '@hologram-dimension/text-input';
import {
  bulkUpdateOverage as bulkUpdateOverage_,
  fetchSlimDevices as fetchSlimDevices_,
} from 'common-js/reducers/devices/actions';
import { required } from 'common-js/utils/validation/validators';
import { noop } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { BannerNotificationType } from 'types/dimension';
import FormHOC from './FormHOC';
import { formGenerator } from './formGenerator';

const unitToBytes = {
  KB: 1_000,
  MB: 1_000_000,
  GB: 1_000_000_000,
};

const customUnitOptions = Object.keys(unitToBytes).map((option) => ({
  label: option,
  value: unitToBytes[option],
}));

interface OutcomeCalloutProps {
  variant: BannerNotificationType;
  text: string;
  devices: Array<{ deviceid: DeviceId; name: string }>;
}
const OutcomeCallout = ({ variant, text, devices }: OutcomeCalloutProps) => (
  <BannerNotification variant={variant}>
    <>
      <div>{text}</div>
      <ul className="BulkUpdateOverageForm__device-list">
        {(devices ?? []).map((device) => (
          <li key={`${device.deviceid}-${device.name}`}>{device.name}</li>
        ))}
      </ul>
    </>
  </BannerNotification>
);

interface BulkUpdateOverageFormProps {
  form: any;
  resetState: AnyFunction;
  setFields: AnyFunction;
  devices: Array<any>;
  customLimit: any;
  customLimitUnit: any;
  limitType: any;
  onFormSubmit: AnyFunction;
  onCancel: AnyFunction;
  formProcessing?: boolean;
  serverError?: any;
  isDirty: boolean;
  isSuccessful: boolean;
  isProcessing: boolean;
  successObject: {
    successes: Array<{
      deviceId: DeviceId;
      item: DeviceId | string;
    }>;
  };
}

interface BulkUpdateOverageFormState {}

class BulkUpdateOverageForm extends React.Component<
  BulkUpdateOverageFormProps,
  BulkUpdateOverageFormState
> {
  componentDidMount() {
    const { resetState, setFields, devices } = this.props;
    resetState();

    // determine SIM's initial usage limit to pre-populate form for a single SIM
    if (
      devices &&
      devices.length === 1 &&
      devices[0].links?.cellular &&
      devices[0].links.cellular.length > 0
    ) {
      const currentOverageLimit = devices[0].links.cellular[0].overagelimit;
      const isUnlimited = currentOverageLimit === -1;

      let customLimit;
      let customLimitUnit = unitToBytes.MB;

      switch (true) {
        case currentOverageLimit >= unitToBytes.GB:
          customLimit = currentOverageLimit / unitToBytes.GB;
          customLimitUnit = unitToBytes.GB;
          break;
        case currentOverageLimit >= unitToBytes.MB:
          customLimit = currentOverageLimit / unitToBytes.MB;
          customLimitUnit = unitToBytes.MB;
          break;
        case currentOverageLimit >= unitToBytes.KB:
          customLimit = currentOverageLimit / unitToBytes.KB;
          customLimitUnit = unitToBytes.KB;
          break;
        default:
      }
      setFields({
        limitType: isUnlimited ? '-1' : 'custom',
        customLimit: isUnlimited ? '' : customLimit,
        customLimitUnit,
      });
    }
  }

  getDeviceById(deviceId: DeviceId) {
    const { devices } = this.props;

    const device = (devices || []).find((d) => d.id === deviceId);

    return (
      device || {
        name: '',
        sim_number: '',
      }
    );
  }

  render() {
    const {
      form,
      customLimit,
      customLimitUnit,
      limitType,
      onFormSubmit,
      formProcessing = false,
      serverError = null,
      isDirty,
      isSuccessful,
      isProcessing,
      successObject,
      onCancel = noop,
      devices,
    } = this.props;

    // Note: successObject.successes returns a .linkId field but devices has a .link_id field
    const successfulDevices = (successObject.successes ?? []).map((device) => device.deviceId);
    // Note: the API doesn't return device names in the `failures` list so we need to filter instead
    const failedDevices = devices.filter((device) => !successfulDevices.includes(device.id));

    const showDone = isSuccessful && successfulDevices.length > 0;

    return (
      <>
        <div className="BulkUpdateOverageForm">
          <Form className="BulkUpdateOverageForm__usage-limit-form" onSubmit={onFormSubmit}>
            <div className="BulkUpdateOverageForm__behavior">
              <p className="BulkUpdateOverageForm__label">Usage behavior</p>
              <p className="BulkUpdateOverageForm__description">
                {devices.length > 1 ? 'These SIMS' : 'This SIM'} can use{' '}
                <strong>
                  {form.limitType.value === '-1' && 'unlimited data without being paused'}
                  {form.limitType.value === 'custom' &&
                    `${form.customLimit.value === '' ? '0' : form.customLimit.value} ${Object.keys(unitToBytes).find((key) => unitToBytes[key] === parseInt(form.customLimitUnit.value, 10))} over the data included in ${devices.length > 1 ? 'their respective data plans' : 'its data plan'}`}
                </strong>
                .
              </p>
            </div>
            <div className="BulkUpdateOverageForm__form-fields">
              <SelectField
                className="BulkUpdateOverageForm__dropdown"
                inputProps={limitType}
                label="Change usage limit"
                name="usagelimit"
                options={[
                  {
                    label: 'Unlimited',
                    value: '-1',
                  },
                  {
                    label: 'Custom amount',
                    value: 'custom',
                  },
                ]}
                helpText={
                  form.limitType.value === 'custom'
                    ? 'The smallest allowed data limit is 1KB'
                    : undefined
                }
              />

              {form.limitType.value === 'custom' && (
                <>
                  <FormField
                    className="BulkUpdateOverageForm__custom-container"
                    label="Custom amount"
                    hiddenLabel
                    invalid={!!form.customLimit.error}
                    controlsLayoutDirection="row"
                    controlsLayoutGap="none"
                    validationMessage={form.customLimit.error}
                  >
                    {({ primaryControlId, ariaDescribedbyIds, invalid }) => (
                      <>
                        <TextInput
                          id={primaryControlId}
                          name="customLimit"
                          aria-describedby={ariaDescribedbyIds}
                          aria-invalid={invalid ? 'true' : 'false'}
                          placeholder="Data amount"
                          inputProps={customLimit}
                        />
                        <Select
                          id="customLimitUnit"
                          className="BulkUpdateOverageForm__custom-unit"
                          name="customLimitUnit"
                          aria-describedby={ariaDescribedbyIds}
                          aria-invalid={invalid ? 'true' : 'false'}
                          options={customUnitOptions}
                          inputProps={customLimitUnit}
                        />
                      </>
                    )}
                  </FormField>
                  <BannerNotification variant="caution" prefixOverride="Note">
                    Data usage limits set here are applied{' '}
                    <strong>over the data included in the SIM’s data plan</strong>. For example:
                    <ul>
                      <li>A SIM on a Pay as you Go data plan will be paused at 50 MB.</li>
                      <li>
                        A SIM on a Pay Monthly data plan with included data will be paused at the
                        included data amount + the limit set here. A SIM on a plan with 250 MB of
                        included data will be paused at 300 MB.
                      </li>
                    </ul>
                  </BannerNotification>
                </>
              )}
            </div>

            {serverError && (
              <BannerNotification variant="system-error">{serverError}</BannerNotification>
            )}

            {isSuccessful && !isProcessing && successObject && !isDirty && (
              <div className="BulkUpdateOverageForm__callouts">
                {successObject?.successes?.length > 0 && (
                  <OutcomeCallout
                    variant="success"
                    text="The data usage limit was changed for:"
                    devices={successObject.successes.map((device) =>
                      this.getDeviceById(device.deviceId)
                    )}
                  />
                )}
                {failedDevices.length > 0 && (
                  <OutcomeCallout
                    variant="error"
                    text="There was an error updating the data usage limit for:"
                    devices={failedDevices.map((device) => this.getDeviceById(device.deviceId))}
                  />
                )}
              </div>
            )}
          </Form>
        </div>
        <div className="BulkDataActionModal__footer">
          <Button onClick={onCancel} variant="secondary" disabled={formProcessing}>
            {showDone ? 'Done' : 'Cancel'}
          </Button>
          {!showDone && (
            <Button type="submit" loading={formProcessing} onClick={onFormSubmit}>
              Update
            </Button>
          )}
        </div>
      </>
    );
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export const config = formGenerator({
  form: 'BulkUpdateOverageForm',
  fields: ['limitType', 'customLimit', 'customLimitUnit'],
  validators: {
    customLimit: [
      required('Please enter data usage limit.', (context) => context.limitType.value === 'custom'),
    ],
  },
  initialState: () => ({
    limitType: {
      value: '-1',
    },
    customLimit: {
      value: '',
    },
    customLimitUnit: {
      value: unitToBytes.MB,
    },
  }),
  onSubmit: (form, actionTypes, dispatch, state, HOC) => {
    const customLimitValue = parseInt(form.customLimit.value || 0, 10) * form.customLimitUnit.value;
    const dataUsageLimitValue =
      form.limitType.value === 'custom' ? customLimitValue : form.limitType.value;

    return HOC.props
      .bulkUpdateOverage(
        HOC.props.devices.map((device) => device.id),
        parseInt(dataUsageLimitValue, 10)
      )
      .then((data) => {
        dispatch({
          type: actionTypes.SUCCESS,
          successObject: {
            successes: data.successes,
            failures: data.failures,
          },
        });

        HOC.props.fetchSlimDevices(HOC.props.devices.map((device) => device.id));
      })
      .catch((errorMessage) => {
        dispatch({
          type: actionTypes.ERROR,
          errorMessage,
        });

        return Promise.reject(errorMessage);
      });
  },
});

const BulkUpdateOverageFormHoC = connect(null, (dispatch: any) =>
  bindActionCreators(
    {
      bulkUpdateOverage: bulkUpdateOverage_,
      fetchSlimDevices: fetchSlimDevices_,
    },
    dispatch
  )
)(FormHOC(BulkUpdateOverageForm, config)) as any;

export default BulkUpdateOverageFormHoC;
